import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import IsometricDigitalCheckIn from '../components/IsometricDigitalCheckIn';
import { ClipboardList, Smartphone, CheckCircle, Clock, FileText, Send, QrCode } from 'lucide-react';
import { useSalesTracking } from '../hooks/useSalesTracking';
import { usePageTitle } from '../hooks/usePageTitle';
import { trackProductPageView } from '../utils/analytics';
import { APP_NAME } from '../config/branding';

const MobileForms: React.FC = () => {
  usePageTitle('Digital Check-In Forms for Veterinary Practices');

  useSalesTracking();

  useEffect(() => {
    trackProductPageView('mobile_forms');
  }, []);

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <div className="mb-4">
              <span className="inline-block px-3 py-1 bg-rose-100 text-rose-600 text-sm font-medium rounded-full">
                Mobile Forms
              </span>
            </div>

            <h1 className="text-5xl font-bold text-gray-900 mb-6">
              Digital check-in, paperless practice
            </h1>

            <p className="text-xl text-gray-600 mb-8">
              Send intake forms via text before appointments. Clients complete them on their phone—no clipboards, no data entry, no waiting room bottlenecks.
            </p>

            <div className="flex gap-4 mb-8">
              <Link
                to="/pricing"
                className="bg-rose-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-rose-700 transition-colors"
              >
                See pricing
              </Link>
              <Link
                to="/contact"
                className="bg-gray-100 text-gray-900 px-6 py-3 rounded-lg font-medium hover:bg-gray-200 transition-colors"
              >
                Request demo →
              </Link>
            </div>
          </div>

          <IsometricDigitalCheckIn />
        </div>

        {/* Feature Highlights */}
        <div className="grid md:grid-cols-3 gap-6 mb-16">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-rose-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Send className="w-5 h-5 text-rose-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Send Before Arrival</h3>
              <p className="text-sm text-gray-600">Forms go out automatically via SMS before the appointment.</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Smartphone className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Mobile-First Design</h3>
              <p className="text-sm text-gray-600">Forms are designed to be fast and easy on any phone.</p>
            </div>
          </div>

          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FileText className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900 mb-1">Attach to Record</h3>
              <p className="text-sm text-gray-600">Responses are automatically saved to the patient's encounter.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Form Types Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">Forms for Every Workflow</h2>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="w-12 h-12 bg-rose-100 rounded-lg flex items-center justify-center mb-4">
                <ClipboardList className="w-6 h-6 text-rose-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">New Patient Intake</h3>
              <p className="text-gray-600 text-sm">Collect pet history, owner info, and consent before the first visit.</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Pre-Visit Check-In</h3>
              <p className="text-gray-600 text-sm">Update current symptoms, medications, and concerns before each visit.</p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm">
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                <QrCode className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">In-Lobby QR Code</h3>
              <p className="text-gray-600 text-sm">Walk-ins scan a QR code to check in on their phone.</p>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works Section */}
      <div className="py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-12 text-center">How It Works</h2>

          <div className="grid md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-rose-600">1</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Create Form</h3>
              <p className="text-sm text-gray-600">Build custom forms or use our templates.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-rose-600">2</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Set Trigger</h3>
              <p className="text-sm text-gray-600">Send 24 hours before appointment or on demand.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-rose-600">3</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Client Completes</h3>
              <p className="text-sm text-gray-600">They fill it out on their phone in minutes.</p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl font-bold text-rose-600">4</span>
              </div>
              <h3 className="font-semibold text-gray-900 mb-2">Data Ready</h3>
              <p className="text-sm text-gray-600">Responses are attached to the encounter automatically.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-5xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Why Go Paperless</h2>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3 p-4 bg-white rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Save 5-10 minutes per appointment</h3>
                <p className="text-gray-600">No more transcribing paper forms or deciphering handwriting.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-white rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Reduce waiting room congestion</h3>
                <p className="text-gray-600">Clients arrive ready to go, not filling out forms.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-white rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Never lose a form</h3>
                <p className="text-gray-600">Digital forms are automatically saved and searchable.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-4 bg-white rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-500 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-gray-900">Collect signatures digitally</h3>
                <p className="text-gray-600">Consent forms are signed on the phone—legally binding.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="py-16 bg-gray-900">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Ditch the clipboard
          </h2>
          <p className="text-xl text-gray-300 mb-8">
            Join practices that have streamlined check-in with digital forms.
          </p>
          <div className="flex gap-4 justify-center">
            <Link
              to="/pricing"
              className="bg-rose-600 text-white px-8 py-3 rounded-lg font-medium hover:bg-rose-700 transition-colors"
            >
              See Plans & Pricing
            </Link>
            <Link
              to="/contact"
              className="bg-gray-800 text-white px-8 py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors border border-gray-700"
            >
              Talk to sales
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileForms;

